/**
 * Configuration the Go shell injects into the document at request time.
 *
 * Read from a `<script type="application/json">` block rather than from
 * `import.meta.env`, so a contract address change is a restart and not a
 * rebuild. That is the same reason the claim portal does it, and it matters
 * more here: the hub is redeployed against a fresh local chain constantly, and
 * baking addresses into the bundle would mean rebuilding the SPA every time
 * Anvil restarts.
 *
 * Deliberately absent: the module addresses. Those come from `/api/utilities`,
 * which reads them from the registry — a module address baked into the page is
 * one the registry can deprecate without the page noticing, and the user then
 * pays a contract that reverts.
 */

export type RuntimeConfig = {
  chainId: number;
  networkName: string;
  /** Empty on a local chain, where there is nothing to link to. */
  explorerBase: string;
  asqToken: `0x${string}`;
  utilityRegistry: `0x${string}`;
  walletConnectProjectId: string;
  portalOrigin: string;
  /** Blocks the server waits for before it will verify a payment. */
  confirmations: number;
  devToolsEnabled: boolean;
};

const CONFIG_ELEMENT_ID = "asetqu-config";

/**
 * Fallback for `vite preview` and for tests, where no Go shell has run.
 *
 * The zero addresses are load-bearing: they make every registry read fail
 * visibly rather than silently pointing at nothing, so "you are looking at the
 * Vite port" is diagnosable instead of looking like a broken deployment.
 */
const UNCONFIGURED: RuntimeConfig = {
  chainId: 31337,
  networkName: "Unconfigured",
  explorerBase: "",
  asqToken: "0x0000000000000000000000000000000000000000",
  utilityRegistry: "0x0000000000000000000000000000000000000000",
  walletConnectProjectId: "",
  portalOrigin: "",
  confirmations: 1,
  devToolsEnabled: false,
};

let cached: RuntimeConfig | null = null;

export function runtimeConfig(): RuntimeConfig {
  if (cached) return cached;

  const element = document.getElementById(CONFIG_ELEMENT_ID);
  if (!element?.textContent) {
    cached = UNCONFIGURED;
    return cached;
  }

  try {
    cached = { ...UNCONFIGURED, ...(JSON.parse(element.textContent) as Partial<RuntimeConfig>) };
  } catch {
    // A malformed block is a bug in the shell, not something to recover from
    // silently — but crashing the whole app would hide it behind a blank page.
    cached = UNCONFIGURED;
  }
  return cached;
}

/** True when the shell never ran, which means nothing on-chain will work. */
export function isUnconfigured(config: RuntimeConfig): boolean {
  return config.utilityRegistry === UNCONFIGURED.utilityRegistry;
}

export function explorerTx(hash: string): string {
  const { explorerBase } = runtimeConfig();
  return explorerBase ? `${explorerBase}/tx/${hash}` : "";
}

export function explorerAddress(address: string): string {
  const { explorerBase } = runtimeConfig();
  return explorerBase ? `${explorerBase}/address/${address}` : "";
}
